import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from './Api';

const AI_KEYS = ['chatgpt', 'claude', 'gemini'];

const AI_META = {
  chatgpt: { titel: 'ChatGPT', cssClass: 'chatgpt-review' },
  claude: { titel: 'Claude', cssClass: 'claude-review' },
  gemini: { titel: 'Gemini', cssClass: 'gemini-review' },
};

const initialSlot = () => ({ status: 'idle', data: null, error: null, startedAt: null });
const initialTrio = () => ({
  chatgpt: initialSlot(),
  claude: initialSlot(),
  gemini: initialSlot(),
});

const Sommeliere = () => {
  const [empfehlungsAnfrage, setEmpfehlungsAnfrage] = useState('');
  const [empfehlungsErgebnis, setEmpfehlungsErgebnis] = useState(initialTrio());

  const [bewertungsBeschreibung, setBewertungsBeschreibung] = useState('');
  const [bewertungsErgebnis, setBewertungsErgebnis] = useState(initialTrio());

  const empfehlungLoading = Object.values(empfehlungsErgebnis).some((s) => s.status === 'loading');
  const bewertungLoading = Object.values(bewertungsErgebnis).some((s) => s.status === 'loading');

  // Re-render alle 500ms, solange irgendein Panel lädt — treibt die Live-Timer an.
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!empfehlungLoading && !bewertungLoading) return undefined;
    const id = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(id);
  }, [empfehlungLoading, bewertungLoading]);

  const elapsed = (startedAt) => ((now - startedAt) / 1000).toFixed(1);

  const fireTrioRequests = (url, payload, setTrio) => {
    const startedAt = Date.now();
    setTrio({
      chatgpt: { status: 'loading', data: null, error: null, startedAt },
      claude: { status: 'loading', data: null, error: null, startedAt },
      gemini: { status: 'loading', data: null, error: null, startedAt },
    });
    AI_KEYS.forEach((ki) => {
      api
        .post(`${url}/${ki}`, payload)
        .then((res) => {
          setTrio((prev) => ({
            ...prev,
            [ki]: {
              ...prev[ki],
              status: res.data?.error ? 'error' : 'done',
              data: res.data,
              error: res.data?.error || null,
            },
          }));
        })
        .catch((err) => {
          setTrio((prev) => ({
            ...prev,
            [ki]: {
              ...prev[ki],
              status: 'error',
              error: err?.message || 'Anfrage fehlgeschlagen',
            },
          }));
        });
    });
  };

  const handleEmpfehlungSubmit = (e) => {
    e.preventDefault();
    if (!empfehlungsAnfrage.trim()) return;
    fireTrioRequests('/auth/sommeliere/empfehlung', { anfrage: empfehlungsAnfrage }, setEmpfehlungsErgebnis);
  };

  const handleBewertungSubmit = (e) => {
    e.preventDefault();
    if (!bewertungsBeschreibung.trim()) return;
    fireTrioRequests('/auth/sommeliere/bewertung', { beschreibung: bewertungsBeschreibung }, setBewertungsErgebnis);
  };

  const LoadingRow = ({ label, startedAt }) => (
    <p className="text-muted mb-0">
      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
      {label}… {elapsed(startedAt)}s
    </p>
  );

  const EmpfehlungPanel = ({ kiKey, slot }) => {
    const { titel, cssClass } = AI_META[kiKey];
    return (
      <div className={`ai-review-section ${cssClass} mb-4`}>
        <h5>{titel} Empfehlung</h5>
        {slot.status === 'loading' && <LoadingRow label="Empfehlung wird erstellt" startedAt={slot.startedAt} />}
        {slot.status === 'error' && (
          <p className="text-muted">{slot.error || 'Keine Empfehlung verfügbar'}</p>
        )}
        {slot.status === 'done' && slot.data?.weine?.length > 0 && (
          <>
            {slot.data.weine.map((wein, index) => (
              <div key={wein.id} className="mb-3">
                {slot.data.rollen?.[index] && (
                  <p className="text-muted mb-1">
                    <strong>{slot.data.rollen[index]}</strong>
                  </p>
                )}
                <p>
                  <strong>Empfohlener Wein:</strong>{' '}
                  <Link to={`/wein/${wein.id}`}>
                    {wein.winzer} - {wein.name} ({wein.jahr})
                  </Link>
                </p>
                {slot.data.begruendungen?.[index] && (
                  <div className="ai-review-content">
                    <p>{slot.data.begruendungen[index]}</p>
                  </div>
                )}
                {index < slot.data.weine.length - 1 && <hr />}
              </div>
            ))}
          </>
        )}
        {slot.status === 'done' && !(slot.data?.weine?.length > 0) && (
          <p className="text-muted">Keine Empfehlung verfügbar</p>
        )}
      </div>
    );
  };

  const renderBewertungLines = (text) =>
    text
      .replace(/\r\n/g, '\n')
      .replace(/\r/g, '\n')
      .split('\n')
      .filter((line) => line.trim())
      .map((line, index) => {
        if (line.includes(':')) {
          const colonIndex = line.indexOf(':');
          const key = line.substring(0, colonIndex);
          const value = line.substring(colonIndex + 1).trim();
          return (
            <p key={index}>
              <strong>{key}:</strong> {value}
            </p>
          );
        }
        return <p key={index}>{line}</p>;
      });

  const BewertungPanel = ({ kiKey, slot }) => {
    const { titel, cssClass } = AI_META[kiKey];
    return (
      <div className={`ai-review-section ${cssClass} mb-4`}>
        <h5>{titel} Sommelier-Bewertung</h5>
        {slot.status === 'loading' && <LoadingRow label="Bewertung wird erstellt" startedAt={slot.startedAt} />}
        {slot.status === 'error' && (
          <p className="text-muted">{slot.error || 'Keine Bewertung verfügbar'}</p>
        )}
        {slot.status === 'done' && slot.data?.text && (
          <div className="ai-review-content">{renderBewertungLines(slot.data.text)}</div>
        )}
        {slot.status === 'done' && !slot.data?.text && (
          <p className="text-muted">Keine Bewertung verfügbar</p>
        )}
      </div>
    );
  };

  const anyEmpfehlungActivity = Object.values(empfehlungsErgebnis).some((s) => s.status !== 'idle');
  const anyBewertungActivity = Object.values(bewertungsErgebnis).some((s) => s.status !== 'idle');

  return (
    <div className="container" style={{ maxWidth: '1200px' }}>
      <h2>Sommelière</h2>
      <p className="text-muted">
        Lassen Sie sich von KI-Sommeliers beraten - powered by ChatGPT, Claude und Gemini
      </p>

      {/* Feature 1: Weinempfehlung */}
      <div className="card mb-5 mt-4">
        <div className="card-body">
          <h3 className="card-title">Welcher Wein für/zu...</h3>
          <p className="text-muted">
            Beschreiben Sie einen Anlass, ein Gericht oder eine Stimmung - die KI-Sommeliers
            empfehlen Ihnen passende Weine aus Ihrem Regal.
          </p>

          <form onSubmit={handleEmpfehlungSubmit}>
            <div className="mb-3">
              <label htmlFor="empfehlungsAnfrage" className="form-label">
                Ihre Anfrage
              </label>
              <textarea
                id="empfehlungsAnfrage"
                className="form-control"
                rows="3"
                value={empfehlungsAnfrage}
                onChange={(e) => setEmpfehlungsAnfrage(e.target.value)}
                placeholder="z.B. 'Wein zu einem saftigen Steak' oder 'Wein für einen gemütlichen Abend'"
                disabled={empfehlungLoading}
              />
            </div>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={empfehlungLoading || !empfehlungsAnfrage.trim()}
            >
              {empfehlungLoading ? 'Wird analysiert...' : 'Empfehlung erhalten'}
            </button>
          </form>

          {anyEmpfehlungActivity && (
            <div className="mt-4">
              <hr />
              <h4>Empfehlungen</h4>
              {AI_KEYS.map((ki) => (
                <EmpfehlungPanel key={ki} kiKey={ki} slot={empfehlungsErgebnis[ki]} />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Feature 2: Weinbewertung */}
      <div className="card mb-5">
        <div className="card-body">
          <h3 className="card-title">Bewerte den Wein mit den folgenden Informationen</h3>
          <p className="text-muted">
            Beschreiben Sie einen Wein mit den Informationen, die Sie haben. Die KI-Sommeliers
            bewerten den Wein basierend auf Ihrer Beschreibung. Sie können vage Angaben machen
            (z.B. "rot, dunkle Beeren, Südhalbkugel") oder konkrete Details nennen (z.B.
            "Klump, Grauburgunder, GG, 2018").
          </p>

          <form onSubmit={handleBewertungSubmit}>
            <div className="mb-3">
              <label htmlFor="bewertungsBeschreibung" className="form-label">
                Weinbeschreibung
              </label>
              <textarea
                id="bewertungsBeschreibung"
                className="form-control"
                rows="4"
                value={bewertungsBeschreibung}
                onChange={(e) => setBewertungsBeschreibung(e.target.value)}
                placeholder="z.B. 'Rotwein, dunkle Beeren, Südhalbkugel' oder 'Klump, Grauburgunder, GG, 2018'"
                disabled={bewertungLoading}
              />
            </div>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={bewertungLoading || !bewertungsBeschreibung.trim()}
            >
              {bewertungLoading ? 'Wird bewertet...' : 'Bewertung erhalten'}
            </button>
          </form>

          {anyBewertungActivity && (
            <div className="mt-4">
              <hr />
              <h4>Bewertungen</h4>
              {AI_KEYS.map((ki) => (
                <BewertungPanel key={ki} kiKey={ki} slot={bewertungsErgebnis[ki]} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Sommeliere;
